/**
 * Componente KPI: Top 20 Itens com Maior Tempo de Execução (Flow Queue)
 * 
 * Exibe os itens com maior latência das filas PGMQ de flows,
 * permitindo identificar processos lentos ou possivelmente travados.
 */

import { useState, Fragment } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Turtle, AlertTriangle, Clock, FileJson, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useFlowQueueSlowestItemsPgmq, FlowQueueSlowestItem } from '@/hooks/useFlowQueueMonitorPgmq';

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Retorna o ícone de medalha para os 3 primeiros lugares
 */
function getRankIcon(index: number): string {
  switch (index) {
    case 0: return '🥇';
    case 1: return '🥈';
    case 2: return '🥉';
    default: return `${index + 1}`;
  }
}

/**
 * Retorna o badge de status com cores apropriadas
 */
function getStatusBadge(status: string, processingTimeMs: number) {
  // Considera "stuck" se em processing há mais de 60 segundos
  const isStuck = status === 'processing' && processingTimeMs > 60000;
  
  const statusConfig: Record<string, { label: string; className: string }> = {
    archived: { 
      label: 'Arquivado', 
      className: 'bg-muted text-muted-foreground border-muted' 
    },
    processing: { 
      label: isStuck ? '⚠️ Proc.' : 'Proc.', 
      className: isStuck 
        ? 'bg-destructive/10 text-destructive border-destructive/30' 
        : 'bg-yellow-500/10 text-yellow-600 border-yellow-500/30' 
    },
    pending: { 
      label: 'Pendente', 
      className: 'bg-blue-500/10 text-blue-600 border-blue-500/30' 
    },
  };
  
  const config = statusConfig[status] || statusConfig.pending;
  
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

/**
 * Retorna o badge de provider com cores específicas
 */
function getProviderBadge(providerSlug: string | null) {
  const colorMap: Record<string, string> = {
    'z-api': 'border-green-500 text-green-500',
    'evolution': 'border-blue-500 text-blue-500',
    'chatwoot': 'border-orange-500 text-orange-500',
    'wuzapi': 'border-purple-500 text-purple-500',
    'notificame': 'border-cyan-500 text-cyan-500',
  };

  return (
    <Badge variant="outline" className={colorMap[providerSlug || ''] || 'border-muted-foreground text-muted-foreground'}>
      {providerSlug?.toUpperCase() || 'N/A'}
    </Badge>
  );
}

/**
 * Formata o telefone para exibição truncada
 */
function formatPhone(phone: string | undefined): string {
  if (!phone) return '-';
  if (phone.length > 13) {
    return `${phone.substring(0, 8)}...`;
  }
  return phone;
}

// ============================================================================
// SUB-COMPONENTES
// ============================================================================

/**
 * Linha individual da tabela de itens lentos com payload expansível
 */
function SlowestItemRow({ item, index }: { item: FlowQueueSlowestItem; index: number }) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Destaque visual para itens muito lentos (> 30s)
  const isVerySlow = item.processing_time_ms > 30000;
  const hasPayload = item.message && Object.keys(item.message).length > 0;
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} asChild>
      <Fragment>
        <TableRow className={cn(
          isVerySlow ? 'bg-destructive/5' : '',
          isOpen && 'bg-muted/20'
        )}>
          {/* Ranking */}
          <TableCell className="text-center py-1.5">
            <span className={index < 3 ? 'text-sm' : 'text-xs text-muted-foreground'}>
              {getRankIcon(index)}
            </span>
          </TableCell>
          
          {/* Tempo de Processamento */}
          <TableCell className="font-mono text-xs py-1.5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1">
                    {isVerySlow && <AlertTriangle className="h-3 w-3 text-destructive" />}
                    <span className={isVerySlow ? 'text-destructive font-semibold' : ''}>
                      {item.processing_time_formatted}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{item.processing_time_ms.toFixed(0)}ms</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
          
          {/* ID (msg_id) */}
          <TableCell className="font-mono text-xs text-muted-foreground py-1.5">
            {item.msg_id}
          </TableCell>
          
          {/* Status */}
          <TableCell className="py-1.5">
            {getStatusBadge(item.status, item.processing_time_ms)}
          </TableCell>
          
          {/* Provider */}
          <TableCell className="py-1.5">
            {getProviderBadge(item.provider_slug)}
          </TableCell>
          
          {/* Action */}
          <TableCell className="text-xs py-1.5">
            <Badge variant="outline" className="font-mono text-[10px] px-1.5 py-0">
              {item.action || '-'}
            </Badge>
          </TableCell>
          
          {/* Tenant */}
          <TableCell className="max-w-[100px] truncate text-xs py-1.5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{item.tenant_name || 'N/A'}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{item.tenant_name}</p>
                  {item.tenant_id && <p className="text-xs text-muted-foreground">{item.tenant_id}</p>}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
          
          {/* Telefone */}
          <TableCell className="font-mono text-xs py-1.5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{formatPhone(item.phone)}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{item.phone || 'Não disponível'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
          
          {/* Flow (flow_name) */}
          <TableCell className="text-xs text-muted-foreground max-w-[80px] truncate py-1.5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{item.flow_name || '-'}</span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{item.flow_name || 'Sem flow associado'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
          
          {/* Tentativas (read_ct) */}
          <TableCell className="text-center text-xs py-1.5">
            <span className={item.read_ct >= 3 ? 'text-destructive font-bold' : ''}>
              {item.read_ct}/3
            </span>
          </TableCell>
          
          {/* Criado (enqueued_at) */}
          <TableCell className="text-muted-foreground text-xs py-1.5">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>
                      {formatDistanceToNow(new Date(item.enqueued_at), { 
                        addSuffix: true, 
                        locale: ptBR 
                      })}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{new Date(item.enqueued_at).toLocaleString('pt-BR')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </TableCell>
          
          {/* Payload (expansível) */}
          <TableCell className="py-1.5">
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                {isOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              </Button>
            </CollapsibleTrigger>
          </TableCell>
        </TableRow>
        
        {/* Linha expandida com payload */}
        <CollapsibleContent asChild>
          <TableRow className="bg-muted/10 hover:bg-muted/10">
            <TableCell colSpan={12} className="p-0">
              <div className="p-4 space-y-2 border-t border-border/50">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <FileJson className="h-4 w-4 text-primary" />
                  Payload
                </div>
                {hasPayload ? (
                  <pre className="text-xs bg-muted/50 p-3 rounded-md overflow-x-auto text-muted-foreground max-h-[300px] overflow-y-auto">
                    {JSON.stringify(item.message, null, 2)}
                  </pre>
                ) : (
                  <p className="text-sm text-muted-foreground">Sem payload</p>
                )}
              </div>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </Fragment>
    </Collapsible>
  );
}

/**
 * Estado de loading da tabela
 */
function LoadingSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="flex gap-4">
          <Skeleton className="h-8 w-[50px]" />
          <Skeleton className="h-8 w-[80px]" />
          <Skeleton className="h-8 w-[90px]" />
          <Skeleton className="h-8 w-[80px]" />
          <Skeleton className="h-8 flex-1" />
          <Skeleton className="h-8 w-[100px]" />
          <Skeleton className="h-8 w-[100px]" />
        </div>
      ))}
    </div>
  );
}

/**
 * Estado vazio da tabela
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
      <Turtle className="h-12 w-12 mb-2 opacity-50" />
      <p className="text-sm">Nenhum item encontrado nas últimas 24 horas</p>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function FlowQueueSlowestItems() {
  const { data: items, isLoading, error } = useFlowQueueSlowestItemsPgmq(20);
  
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Turtle className="h-5 w-5 text-muted-foreground" />
          <CardTitle className="text-lg">Top 20 - Maior Tempo de Execução</CardTitle>
        </div>
        <CardDescription>
          Itens com maior latência (arquivados + em processamento) nas últimas 24h
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton />
        ) : error ? (
          <div className="text-destructive text-sm">
            Erro ao carregar dados: {error.message}
          </div>
        ) : !items || items.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="text-xs">
                  <TableHead className="w-[40px] text-center py-2">#</TableHead>
                  <TableHead className="w-[70px] py-2">Tempo</TableHead>
                  <TableHead className="w-[50px] py-2">ID</TableHead>
                  <TableHead className="w-[80px] py-2">Status</TableHead>
                  <TableHead className="w-[80px] py-2">Provider</TableHead>
                  <TableHead className="w-[80px] py-2">Action</TableHead>
                  <TableHead className="py-2">Tenant</TableHead>
                  <TableHead className="w-[90px] py-2">Telefone</TableHead>
                  <TableHead className="w-[80px] py-2">Flow</TableHead>
                  <TableHead className="w-[50px] py-2">Tent.</TableHead>
                  <TableHead className="w-[90px] py-2">Criado</TableHead>
                  <TableHead className="w-[40px] py-2"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <SlowestItemRow key={item.msg_id} item={item} index={index} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
